/**
 * LA ESCALERA DE FORMATOS DEL GRABADOR (spec §0 "Record: formato"). El primer mime que pase
 * `MediaRecorder.isTypeSupported` Y un `start()` de verdad: Chrome muxea MP4 desde la 126, Safari
 * graba SOLO MP4 (H.264/AAC) y Firefox SOLO WebM. "AAC relies on the platform's hardware encoder":
 * que `isTypeSupported` diga que sí no garantiza que grabe, por eso se prueba arrancando y parando
 * un grabador de sondeo antes de comprometer el formato con el servidor.
 *
 * La lista (`mimeLadder`) es pura y se prueba en vitest/node; la sonda (`probeMime`) toca
 * `MediaRecorder` y solo corre en el navegador.
 */

export const MIME_CANDIDATES: readonly string[] = [
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/** Los candidatos que el navegador DICE soportar, en orden de preferencia. Vacío = no se puede grabar. */
export function mimeLadder(isTypeSupported: (m: string) => boolean, candidates: readonly string[] = MIME_CANDIDATES): string[] {
  const out: string[] = [];
  for (const m of candidates) {
    let ok = false;
    try { ok = isTypeSupported(m) === true; } catch { ok = false; }
    if (ok) out.push(m);
  }
  return out;
}

/** Extensión del archivo crudo según el mime (para el nombre de la descarga local). */
export function extForMime(mime: string): 'mp4' | 'webm' {
  return /^video\/mp4/i.test(mime) ? 'mp4' : 'webm';
}

export type ProbeResult = { mime: string; mimeFallback: boolean };

/**
 * Elige el formato ARRANCANDO de verdad: `new MediaRecorder(stream, { mimeType }).start()` y `stop()`
 * en el acto; los datos de la sonda se descartan. Si `start()` tira (NotSupportedError con el mime
 * "soportado"), se baja un peldaño y se anota `mimeFallback` (spec §7). Devuelve null si ninguno
 * arranca. `Recorder` se inyecta para poder probar la lógica sin navegador.
 */
export function probeMime(
  stream: MediaStream,
  ladder: readonly string[],
  Recorder: { new (s: MediaStream, o: { mimeType: string }): { start(): void; stop(): void; state?: string } } = MediaRecorder,
): ProbeResult | null {
  for (let i = 0; i < ladder.length; i += 1) {
    const mimeType = ladder[i]!;
    let r: { start(): void; stop(): void; state?: string } | null = null;
    try {
      r = new Recorder(stream, { mimeType });
      r.start();
    } catch {
      continue;
    } finally {
      // `stop()` sobre un grabador que nunca arrancó tira InvalidStateError: se ignora.
      try { if (r && r.state !== 'inactive') r.stop(); } catch { /* */ }
    }
    return { mime: mimeType, mimeFallback: i > 0 };
  }
  return null;
}
